import React, { useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Paper,
  Divider,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { authenticatedFetch } from '@services/api/apiClient'
import { apiEndpoints } from '../../../consts'
import { ResponsiveTable } from '@components/TableComponents'
import { StatusChip, ToastNotification } from '@components/atoms'
import { AutoCompleteInput } from '@components/AutoCompleteInput'
import { useFetch, useToast } from '@hooks'
import QAFormTemplateDialog from './components/QAFormTemplateDialog'
import sharedStyles from '@views/settings/qualityAssurance/SettingsQA.module.scss'
import styles from './ManualQAConfig.module.scss'

const QUESTION_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'radio', label: 'Single Choice' },
  { value: 'checkbox', label: 'Multiple Choice' },
  { value: 'select', label: 'Dropdown' },
]

export default function SettingsManualQAConfig() {
  const [saving, setSaving] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingQuestion, setEditingQuestion] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingQuestion, setDeletingQuestion] = useState(null)
  const { toast, showToast, closeToast } = useToast()

  const [formData, setFormData] = useState({
    questionText: '',
    questionType: 'text',
    options: [],
    isRequired: false,
    displayOrder: 0,
    isActive: true,
  })
  const [newOption, setNewOption] = useState({ value: '', label: '' })

  const { data: campaignsWithQuestions, refetch: refetchCampaignsWithQuestions } = useFetch(
    apiEndpoints.qaManualCampaigns,
    {
      initialData: [],
      transform: (data) => data.data || [],
      errorMessage: 'Error fetching campaigns with questions',
    },
  )

  const { data: allCampaigns, refetch: refetchAllCampaigns } = useFetch(apiEndpoints.campaigns, {
    initialData: [],
    transform: (data) => {
      const campaigns = Array.isArray(data) ? data : data.data || []
      return campaigns.map((c) => ({
        name: c.original_name || c.campaign_name || c.name,
        displayName: c.campaign_name || c.original_name || c.name,
      }))
    },
    errorMessage: 'Error fetching all campaigns',
  })

  const {
    data: questions,
    loading: questionsLoading,
    refetch: refetchQuestions,
  } = useFetch(
    `${apiEndpoints.qaManualFormConfig}?campaign=${encodeURIComponent(selectedCampaign?.name || '')}&includeInactive=true`,
    {
      initialData: [],
      skip: !selectedCampaign,
      dependencies: [selectedCampaign?.name],
      transform: (data) => data.data || [],
      errorMessage: 'Failed to fetch questions',
    },
  )

  const handleOpenDialog = (question = null) => {
    if (question) {
      setEditingQuestion(question)
      setFormData({
        questionText: question.questionText,
        questionType: question.questionType,
        options: question.options || [],
        isRequired: question.isRequired,
        displayOrder: question.displayOrder,
        isActive: question.isActive,
      })
    } else {
      setEditingQuestion(null)
      setFormData({
        questionText: '',
        questionType: 'text',
        options: [],
        isRequired: false,
        displayOrder: questions.length,
        isActive: true,
      })
    }
    setNewOption({ value: '', label: '' })
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingQuestion(null)
    setFormData({
      questionText: '',
      questionType: 'text',
      options: [],
      isRequired: false,
      displayOrder: 0,
      isActive: true,
    })
  }

  const handleAddOption = () => {
    if (!newOption.value.trim() || !newOption.label.trim()) {
      showToast('Option value and label are required', 'error')
      return
    }

    setFormData((prev) => ({
      ...prev,
      options: [...prev.options, { ...newOption }],
    }))
    setNewOption({ value: '', label: '' })
  }

  const handleRemoveOption = (index) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }))
  }

  const handleSaveQuestion = async () => {
    /* istanbul ignore next -- guarded by disabled save button */
    if (!formData.questionText.trim()) {
      showToast('Question text is required', 'error')
      return
    }

    const needsOptions = ['radio', 'checkbox', 'select'].includes(formData.questionType)
    if (needsOptions && formData.options.length < 2) {
      showToast('At least 2 options are required for this question type', 'error')
      return
    }

    setSaving(true)

    try {
      const payload = {
        id: editingQuestion?.id,
        campaignName: selectedCampaign.name,
        questionText: formData.questionText,
        questionType: formData.questionType,
        options: needsOptions ? formData.options : null,
        isRequired: formData.isRequired,
        displayOrder: formData.displayOrder,
        isActive: formData.isActive,
      }

      const response = await authenticatedFetch(apiEndpoints.qaManualFormConfig, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error('Failed to save question')
      }

      showToast(editingQuestion ? 'Question updated successfully' : 'Question created successfully')
      handleCloseDialog()
      refetchQuestions()
      refetchCampaignsWithQuestions()
    } catch (err) {
      console.error('Error saving question:', err)
      showToast('Failed to save question', 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteClick = (question) => {
    setDeletingQuestion(question)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!deletingQuestion) return

    setSaving(true)
    try {
      const response = await authenticatedFetch(
        apiEndpoints.qaManualFormConfigItem(deletingQuestion.id),
        {
          method: 'DELETE',
        },
      )

      if (!response.ok) {
        throw new Error('Failed to delete question')
      }

      showToast('Question deleted successfully')
      setDeleteDialogOpen(false)
      setDeletingQuestion(null)
      refetchQuestions()
      refetchCampaignsWithQuestions()
    } catch (err) {
      console.error('Error deleting question:', err)
      showToast('Failed to delete question', 'error')
    } finally {
      setSaving(false)
    }
  }

  const getQuestionCount = (campaignName) => {
    const found = campaignsWithQuestions.find((c) => c.campaignName === campaignName)
    return found ? found.activeCount : 0
  }

  const getTypeLabel = (type) => {
    const found = QUESTION_TYPES.find((t) => t.value === type)
    return found ? found.label : type
  }

  return (
    <div className={styles.root}>
      {/* Campaign Selection */}
      <Card className={styles.card}>
        <CardContent className={sharedStyles.cardContent}>
          <Box className={styles.cardHeader}>
            <Typography variant="h6" className={styles.cardTitle}>
              Select Campaign
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => {
                refetchCampaignsWithQuestions()
                refetchAllCampaigns()
                if (selectedCampaign) refetchQuestions()
              }}
              disabled={questionsLoading}
              className={styles.actionButton}
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Refresh
            </Button>
          </Box>

          <AutoCompleteInput
            options={allCampaigns}
            value={selectedCampaign}
            onChange={(_, newValue) => setSelectedCampaign(newValue)}
            badges={[{ getCount: (option) => getQuestionCount(option.name), label: 'questions' }]}
            placeholder="Search campaigns..."
            noOptionsText="No campaigns found"
            className={styles.campaignAutocomplete}
          />

          {/* Campaign Status */}
          {selectedCampaign && (
            <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
              <Box display="flex" alignItems="center">
                {questions.length > 0 ? (
                  <StatusChip
                    label={`${questions.filter((q) => q.isActive).length} Active Questions`}
                    color="success"
                  />
                ) : (
                  <StatusChip label="No Questions Configured" color="warning" variant="soft" />
                )}
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                disabled={!selectedCampaign}
                className={`${styles.actionButton} ${styles.primaryButton}`}
              >
                Add Question
              </Button>
            </Box>
          )}

          {/* Questions Table */}
          {selectedCampaign && (
            <div style={{ marginTop: 16 }}>
              <ResponsiveTable
                columns={[
                  {
                    id: 'displayOrder',
                    label: 'Order',
                    style: { width: 50 },
                    renderCell: (row) => (
                      <Chip
                        size="small"
                        label={row.displayOrder + 1}
                        style={{ backgroundColor: 'var(--color-bg-light)' }}
                      />
                    ),
                  },
                  {
                    id: 'questionText',
                    label: 'Question',
                    renderCell: (row) => (
                      <>
                        <Typography variant="body2" style={{ fontWeight: 500 }}>
                          {row.questionText}
                        </Typography>
                        {row.options && row.options.length > 0 && (
                          <Typography className={styles.optionsPreview}>
                            Options: {row.options.map((o) => o.label).join(', ')}
                          </Typography>
                        )}
                      </>
                    ),
                  },
                  {
                    id: 'questionType',
                    label: 'Type',
                    renderCell: (row) => (
                      <StatusChip
                        size="small"
                        label={getTypeLabel(row.questionType)}
                        color="info"
                        variant="soft"
                      />
                    ),
                  },
                  {
                    id: 'isRequired',
                    label: 'Required',
                    renderCell: (row) =>
                      row.isRequired ? (
                        <StatusChip size="small" label="Required" color="error" variant="soft" />
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Optional
                        </Typography>
                      ),
                  },
                  {
                    id: 'isActive',
                    label: 'Status',
                    renderCell: (row) => (
                      <StatusChip
                        size="small"
                        label={row.isActive ? 'Active' : 'Inactive'}
                        color={row.isActive ? 'success' : 'neutral'}
                      />
                    ),
                  },
                  {
                    id: 'actions',
                    label: 'Actions',
                    align: 'right',
                    renderCell: (row) => (
                      <>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenDialog(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(row)}
                            style={{ color: 'var(--color-error)' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ),
                  },
                ]}
                data={questions}
                loading={questionsLoading}
                variant="qa"
                emptyState={
                  <Box className={styles.emptyState}>
                    <AddIcon className={styles.emptyIcon} />
                    <Typography variant="h6" gutterBottom>
                      No Questions Configured
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Add your first form question for this campaign
                    </Typography>
                  </Box>
                }
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Campaigns with Questions Summary */}
      <Card className={styles.card}>
        <CardContent className={sharedStyles.cardContent}>
          <Typography variant="h6" className={styles.cardTitle} gutterBottom>
            Campaigns with Form Questions
          </Typography>
          <Divider style={{ margin: '16px 0' }} />

          {campaignsWithQuestions.length === 0 ? (
            <Box className={styles.emptyState} style={{ padding: 32 }}>
              <Typography variant="body1" color="textSecondary">
                No campaigns have form questions configured yet. Select a campaign above to add your
                first question.
              </Typography>
            </Box>
          ) : (
            <Box display="flex" flexWrap="wrap" style={{ gap: 16 }}>
              {campaignsWithQuestions.map((campaign) => (
                <Paper
                  key={campaign.campaignName}
                  onClick={() =>
                    setSelectedCampaign({
                      name: campaign.campaignName,
                      displayName: campaign.campaignName,
                    })
                  }
                  style={{
                    padding: 20,
                    borderRadius: 12,
                    minWidth: 200,
                    cursor: 'pointer',
                    border:
                      selectedCampaign?.name === campaign.campaignName
                        ? '2px solid var(--color-deep-blue)'
                        : '1px solid var(--color-border)',
                    transition: 'all 0.2s ease',
                  }}
                  elevation={0}
                >
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {campaign.campaignName}
                  </Typography>
                  <Box display="flex" alignItems="baseline" style={{ gap: 8 }}>
                    <Typography
                      style={{
                        fontSize: 'var(--font-size-2xl)',
                        fontWeight: 700,
                        color: 'var(--color-deep-blue)',
                      }}
                    >
                      {campaign.activeCount}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      / {campaign.questionCount} total
                    </Typography>
                  </Box>
                  <Typography variant="caption" style={{ color: 'var(--color-success)' }}>
                    active questions
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <QAFormTemplateDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        editingQuestion={editingQuestion}
        classes={styles}
        formData={formData}
        setFormData={setFormData}
        newOption={newOption}
        setNewOption={setNewOption}
        handleAddOption={handleAddOption}
        handleRemoveOption={handleRemoveOption}
        saving={saving}
        onSave={handleSaveQuestion}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={/* istanbul ignore next */ () => setDeleteDialogOpen(false)}
        PaperProps={{ style: { borderRadius: 16, padding: 8 } }}
      >
        <DialogTitle>
          <Typography variant="h6" style={{ color: 'var(--color-error)', fontWeight: 600 }}>
            Delete Question
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 8 }}>
            Are you sure you want to delete this question?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            &ldquo;{deletingQuestion?.questionText}&rdquo;
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: 16 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={saving}
            style={{ backgroundColor: 'var(--color-error)', color: '#fff', borderRadius: 10 }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={closeToast}
      />
    </div>
  )
}
